import { useEffect, useState } from 'react'
import {
  TrendingUp,
  DollarSign,
  Zap,
  Target,
  ArrowRight,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from '../DiscoveryWizard'
import { formatCurrency, formatPercent, formatPayback } from '@/lib/value-calculator'
import { useCases, valueMessages } from '@/lib/discovery-data'

export function ValueStep() {
  const { state, valueMetrics } = useDiscoveryContext()
  const [animatedValues, setAnimatedValues] = useState({
    savings: 0,
    successRate: 0,
    roi: 0,
  })
  const [showDetails, setShowDetails] = useState(false)

  const useCase = useCases.find((u) => u.id === state.useCase)
  const messages = state.useCase ? valueMessages[state.useCase] : []

  // Animate values on mount
  useEffect(() => {
    if (!valueMetrics) return

    const duration = 1500
    const steps = 60
    const interval = duration / steps

    let step = 0
    const timer = setInterval(() => {
      step++
      const progress = step / steps
      const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic

      setAnimatedValues({
        savings: Math.round(valueMetrics.projectedSavings * eased),
        successRate: valueMetrics.projectedSuccessRate * eased,
        roi: valueMetrics.threeYearROI * eased,
      })

      if (step >= steps) {
        clearInterval(timer)
        setShowDetails(true)
      }
    }, interval)

    return () => clearInterval(timer)
  }, [valueMetrics])

  if (!valueMetrics || !useCase) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">Calculating your value...</p>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-12 overflow-auto">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center wizard-fade-in">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm mb-4">
            <Sparkles className="w-4 h-4" />
            Your Value Analysis
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            Here's what Cyclops can deliver
          </h2>
          <p className="text-muted-foreground">
            Based on your {useCase.name.toLowerCase()} operations
          </p>
        </div>

        {/* Primary value metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Annual Savings */}
          <ValueCard
            icon={<DollarSign className="w-6 h-6" />}
            label="Annual Cost Savings"
            value={formatCurrency(animatedValues.savings)}
            sublabel="Recovered from prevented failures"
            accent
            delay={0}
          />

          {/* Success Rate */}
          <ValueCard
            icon={<Target className="w-6 h-6" />}
            label="Mission Success Rate"
            value={`${animatedValues.successRate.toFixed(1)}%`}
            sublabel={`+${valueMetrics.successRateImprovement.toFixed(1)}% improvement`}
            delay={200}
          />

          {/* ROI */}
          <ValueCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="3-Year ROI"
            value={`${Math.round(animatedValues.roi)}%`}
            sublabel={`Payback in ${formatPayback(valueMetrics.paybackPeriodMonths)}`}
            delay={400}
          />
        </div>

        {/* Before/After comparison */}
        {showDetails && (
          <div className="wizard-fade-in">
            <div className="p-6 rounded-lg border border-border/30 bg-secondary/10">
              <h3 className="text-sm font-medium text-muted-foreground mb-4">
                Mission Performance Comparison
              </h3>
              <div className="flex items-center justify-center gap-4">
                {/* Before */}
                <div className="flex-1 text-center p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-xs text-muted-foreground mb-1">Current State</p>
                  <p className="text-3xl font-bold text-destructive">
                    {formatPercent(valueMetrics.currentSuccessRate)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">success rate</p>
                </div>

                {/* Arrow */}
                <div className="flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-primary animate-pulse" />
                </div>

                {/* After */}
                <div className="flex-1 text-center p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-xs text-muted-foreground mb-1">With Cyclops</p>
                  <p className="text-3xl font-bold text-primary">
                    {formatPercent(valueMetrics.projectedSuccessRate)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">success rate</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New capabilities */}
        {showDetails && valueMetrics.newCapabilities.length > 0 && (
          <div className="wizard-fade-in">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">
              New Capabilities Unlocked
            </h3>
            <div className="flex flex-wrap gap-2">
              {valueMetrics.newCapabilities.map((cap, index) => (
                <span
                  key={cap}
                  className="px-3 py-1.5 rounded-full bg-secondary text-sm text-foreground wizard-stagger"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Zap className="w-3 h-3 inline mr-1.5 text-primary" />
                  {cap}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Value messages */}
        {showDetails && messages.length > 0 && (
          <div className="wizard-fade-in pt-4 border-t border-border/20">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">
              Key Benefits for {useCase.name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {messages.map((msg, index) => (
                <div
                  key={msg.id}
                  className="p-4 rounded-lg border border-border/20 wizard-stagger"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <h4 className="text-sm font-medium text-foreground mb-1">{msg.title}</h4>
                  <p className="text-xs text-muted-foreground/70">{msg.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface ValueCardProps {
  icon: React.ReactNode
  label: string
  value: string
  sublabel: string
  accent?: boolean
  delay: number
}

function ValueCard({ icon, label, value, sublabel, accent, delay }: ValueCardProps) {
  return (
    <div
      className={cn(
        'p-6 rounded-lg border text-center wizard-stagger',
        accent
          ? 'border-primary/30 bg-primary/5'
          : 'border-border/30 bg-secondary/10'
      )}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className={cn(
          'w-12 h-12 rounded-lg mx-auto mb-4 flex items-center justify-center',
          accent ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'
        )}
      >
        {icon}
      </div>
      <p className="text-xs text-muted-foreground/70 mb-1">{label}</p>
      <p
        className={cn(
          'text-3xl font-bold mb-1',
          accent ? 'text-primary' : 'text-foreground'
        )}
      >
        {value}
      </p>
      <p className="text-xs text-muted-foreground/60">{sublabel}</p>
    </div>
  )
}

